import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Flame, Mail as MailIcon, Paperclip, Star } from "@fleet/icons";

import { cn } from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { MessageSummary } from "../api";
import { displayFromAddress, formatMailDate } from "./format";
import { MessageListSkeleton } from "./skeletons";

interface MessageListProps {
  messages: MessageSummary[] | undefined;
  loading: boolean;
  /**
   * P23 / CC-60 · I3. The mailbox query failed. Without this third state,
   * a mail server 5xx rendered as "Inbox zero" — the most expensive lie in this
   * product: the user closes the app believing no new mail arrived.
   */
  error?: boolean;
  /** Real retry (`query.refetch()`), not state clearing. */
  onRetry?: () => void;
  activeUid: number | undefined;
  onSelect: (msg: MessageSummary) => void;
  onToggleFlag: (msg: MessageSummary) => void;
  emptyHint?: string;
  /** Inbox-zero streak in days; > 0 renders the celebration badge. */
  inboxZeroStreak?: number;
}

export function MessageList(props: MessageListProps) {
  const { t, locale } = useI18n();

  return (
    <div class="flex min-h-0 flex-1 flex-col" data-testid="mail-message-list">
      {/* The error WRAPS the content block, not chained with it: on
          error `loading` is `false`, so a `when={!loading && !error}` with
          `fallback={skeleton}` would leave error + skeleton simultaneously, forever
          (recipe trap #6). */}
      <Show when={props.error}>
        <div class="flex flex-1 items-center justify-center p-6">
          <QueryErrorState
            testId="mail-messages-error"
            title={t(msg`Couldn't load this folder`)}
            description={t(
              msg`We couldn't reach the mail server. This is a connection problem, not an empty folder.`,
            )}
            onRetry={props.onRetry}
          />
        </div>
      </Show>
      <Show when={!props.error}>
        <Show when={!props.loading} fallback={<MessageListSkeleton />}>
          <Show
            when={(props.messages?.length ?? 0) > 0}
            fallback={
              <div
                class="flex flex-1 flex-col items-center justify-center gap-3 p-6"
                data-testid="mail-messages-empty"
              >
                <EmptyState
                  icon={MailIcon}
                  title={t(msg`Inbox zero`)}
                  description={
                    props.emptyHint ?? t(msg`Nothing in the mailbag — you're all caught up.`)
                  }
                />
                <Show when={(props.inboxZeroStreak ?? 0) > 0}>
                  <p
                    class="flex items-center gap-1.5 rounded-full border border-border/50 bg-card/40 px-3 py-1 text-xs font-medium text-muted-foreground"
                    data-testid="mail-inboxzero-streak"
                  >
                    <Flame class="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                    {(() => {
                      const days = props.inboxZeroStreak ?? 0;
                      return t(msg`Inbox-zero streak: ${days} day(s)`);
                    })()}
                  </p>
                </Show>
              </div>
            }
          >
            <ul class="flex flex-col divide-y divide-border/30 overflow-y-auto">
              <For each={props.messages}>
                {(m) => {
                  const active = () => m.uid === props.activeUid;
                  return (
                    <li>
                      <div
                        class={cn(
                          "group flex cursor-pointer items-start gap-3 border-l-2 py-3 pr-4 pl-[calc(1rem-2px)] transition-colors",
                          active()
                            ? "border-l-accent bg-accent/12"
                            : m.seen
                              ? "border-l-transparent hover:bg-accent/5"
                              : "border-l-accent/50 bg-accent/[0.05] hover:bg-accent/[0.08]",
                        )}
                        onClick={() => props.onSelect(m)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            props.onSelect(m);
                          }
                        }}
                        role="button"
                        tabindex="0"
                        data-testid="mail-message-row"
                        data-uid={m.uid}
                        data-seen={m.seen ? "true" : "false"}
                      >
                        <button
                          type="button"
                          class="mt-0.5 shrink-0 text-muted-foreground/50 hover:text-amber-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onToggleFlag(m);
                          }}
                          aria-label={m.flagged ? t(msg`Unstar`) : t(msg`Star`)}
                          data-testid="mail-flag-toggle"
                        >
                          <Star
                            class={cn("h-4 w-4", m.flagged && "fill-amber-400 text-amber-400")}
                          />
                        </button>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-baseline gap-2">
                            <span
                              class={cn(
                                "min-w-0 flex-1 truncate text-sm",
                                m.seen ? "text-muted-foreground" : "font-semibold text-foreground",
                              )}
                            >
                              {displayFromAddress(m.from)}
                            </span>
                            <span class="shrink-0 text-[11px] text-muted-foreground/70">
                              {formatMailDate(m.date, locale())}
                            </span>
                          </div>
                          <div class="flex items-center gap-1.5">
                            <span
                              class={cn(
                                "min-w-0 flex-1 truncate text-sm",
                                m.seen ? "text-muted-foreground/90" : "font-medium text-foreground",
                              )}
                            >
                              {m.subject || t(msg`(no subject)`)}
                            </span>
                            <Show when={m.hasAttachments}>
                              <Paperclip class="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                            </Show>
                          </div>
                          <p class="truncate text-xs text-muted-foreground/70">{m.snippet}</p>
                        </div>
                        <Show when={!m.seen}>
                          <span
                            class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                            aria-hidden="true"
                          />
                        </Show>
                      </div>
                    </li>
                  );
                }}
              </For>
            </ul>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
